/**
 * lite-gallery.js  (darphane-lite / "Oda Galeri")
 *
 * Lightweight static gallery driver that REPLACES the 92KB canvas compositor
 * (wall-art-gallery-renderer.js) for the lite shop. The lite product carries
 * its finished gallery images in the `oda.gallery` metafield (emitted to the
 * page by product-media-gallery.liquid as a JSON <script>); there is nothing to
 * composite client-side.
 *
 * Data sources (emitted by snippets/product-media-gallery.liquid):
 *   <script type="application/json" id="OdaGalleryData-<section.id>">
 *     {
 *       "hero":     { "<formatKey>": { "<frameKey>": { "url","alt" } } },
 *       "features": { "<formatKey>": [ { "url","alt" }, ... ] },
 *       "lifestyle":[ { "url","alt" }, ... ]
 *     }
 *   </script>
 *   formatKey ∈ art_print|cerceveli_tablo ; frameKey ∈ yok|siyah|beyaz|ahsap
 *   (No `info` key — slot 7 is a per-shop static image, see below.)
 *
 *   <script type="application/json" id="OdaInfoImageData-<section.id>">
 *     [ { "format":"art_print", "url","thumb_url","alt" },
 *       { "format":"cerceveli_tablo", "url","thumb_url","alt" } ]
 *   </script>
 *   The 2 per-format static "info" images set ONCE in the theme editor
 *   (section.settings.info_image_art_print / info_image_cerceveli). Shown on
 *   every product; blank settings are omitted (slot 7 hides for that format).
 *
 * Slot map (matches the 7-slot DOM in product-media-gallery.liquid):
 *   slot 1  hero   ← hero[formatKey][frameKey]                (swaps on format/frame)
 *   slot 7  info   ← OdaInfoImageData[formatKey]  (shown 2nd) (swaps on FORMAT only)
 *   slots 2,3,4    ← lifestyle[0], [1], [2]      (static; format-agnostic)
 *   slot 6  image  ← lifestyle[3]                (static; format-agnostic)
 *   slot 5  video  ← left to wall-art-format-video.js (KEPT)
 *
 * Trigger: 'wallart:selection:changed' from wall-art-picker.js with
 *   detail = { formatKey, frameKey, sizeKey, variantId, price }.
 * The hero slide re-points on format+frame; slot 7 re-points on format only;
 * lifestyle/static slots are set once (they don't depend on the combo).
 */
(function () {
  'use strict';

  const DATA_SCRIPT_SELECTOR = 'script[type="application/json"][id^="OdaGalleryData-"]';
  const INFO_DATA_SCRIPT_SELECTOR = 'script[type="application/json"][id^="OdaInfoImageData-"]';
  const controllers = new Map();

  function readGallery(sectionRoot) {
    const tag = sectionRoot.querySelector ? sectionRoot.querySelector(DATA_SCRIPT_SELECTOR) : null;
    if (!tag) return null;
    try {
      const parsed = JSON.parse(tag.textContent || 'null');
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      return null;
    }
  }

  /** Read the per-format slot-7 info images → { '<formatKey>': {url,thumb_url,alt} }.
   *  Source is an ARRAY of { format, url, thumb_url, alt } from the theme settings.
   *  Returns {} when absent/empty (slot 7 then hides for every format). */
  function readInfoImages(sectionRoot) {
    const tag = sectionRoot.querySelector ? sectionRoot.querySelector(INFO_DATA_SCRIPT_SELECTOR) : null;
    if (!tag) return {};
    let arr;
    try {
      arr = JSON.parse(tag.textContent || '[]');
    } catch {
      return {};
    }
    const byFormat = {};
    if (Array.isArray(arr)) {
      for (const e of arr) {
        if (e && e.format && e.url) byFormat[e.format] = e;
      }
    }
    return byFormat;
  }

  function findSectionRootForGallery(gallery) {
    return (
      gallery.closest('product-info') ||
      gallery.closest('[id^="ProductInfo-"]') ||
      gallery.closest('[id^="MainProduct-"]') ||
      document
    );
  }

  /** Resolve hero/info image for (formatKey, frameKey) with sensible fallbacks:
   *  exact → same format any frame → any format/frame. Returns {url,alt}|null. */
  function resolveCombo(map, formatKey, frameKey) {
    if (!map || typeof map !== 'object') return null;
    const byFrame = map[formatKey];
    if (byFrame && typeof byFrame === 'object') {
      if (frameKey && byFrame[frameKey] && byFrame[frameKey].url) return byFrame[frameKey];
      // Same format, first available frame.
      for (const k of Object.keys(byFrame)) {
        if (byFrame[k] && byFrame[k].url) return byFrame[k];
      }
    }
    // Any format, first available frame.
    for (const f of Object.keys(map)) {
      const bf = map[f];
      if (bf && typeof bf === 'object') {
        for (const k of Object.keys(bf)) {
          if (bf[k] && bf[k].url) return bf[k];
        }
      }
    }
    return null;
  }

  /** Ensure the media container has an <img> we can drive, replacing any
   *  placeholder SVG. Returns the <img> (or null if no container). */
  function ensureImg(container, marker) {
    if (!container) return null;
    let img = container.querySelector('img' + (marker ? `[${marker}]` : ''));
    if (img) return img;
    img = container.querySelector('img');
    if (img) {
      if (marker) img.setAttribute(marker, '');
      return img;
    }
    // No <img> (placeholder SVG only) — replace contents with a fresh <img>.
    const svg = container.querySelector('svg, .placeholder-svg');
    if (svg) svg.remove();
    img = document.createElement('img');
    img.setAttribute('width', '1946');
    img.setAttribute('height', '1946');
    img.setAttribute('loading', 'lazy');
    img.setAttribute('decoding', 'async');
    if (marker) img.setAttribute(marker, '');
    container.appendChild(img);
    return img;
  }

  function setImg(img, image) {
    if (!img || !image || !image.url) return;
    img.setAttribute('src', image.url);
    img.removeAttribute('srcset');
    if (image.alt) img.setAttribute('alt', image.alt);
  }

  /** Ensure a thumbnail <button.thumbnail> has a real <img> we can drive.
   *  When the slot's per-format setting is blank (the common lite case), the
   *  thumbnail <li> is server-rendered with only a placeholder SVG span
   *  (`<span class="media media--transparent">…svg…</span>`) and NO <img>, so a
   *  plain querySelector('img') returns null and the thumbnail never paints.
   *  Mirror ensureImg(): drop the placeholder span/svg and inject an <img>
   *  (styled by Dawn's `.thumbnail img { object-fit:cover }`). Prefers an
   *  existing marked/plain <img>; returns null only if there's no button host. */
  function ensureThumbImg(thumbSlide, marker) {
    if (!thumbSlide) return null;
    let img = thumbSlide.querySelector('img' + (marker ? `[${marker}]` : ''));
    if (img) return img;
    img = thumbSlide.querySelector('img');
    if (img) {
      if (marker) img.setAttribute(marker, '');
      return img;
    }
    const host = thumbSlide.querySelector('button.thumbnail') || thumbSlide.querySelector('button') || thumbSlide;
    if (!host) return null;
    // Remove the placeholder SVG (and its media--transparent wrapper) so the
    // thumbnail shows the real image instead of the apparel silhouette.
    const placeholder = host.querySelector('.media--transparent, svg, .placeholder-svg');
    if (placeholder) {
      const wrapper = placeholder.closest('.media--transparent');
      (wrapper || placeholder).remove();
    }
    img = document.createElement('img');
    img.setAttribute('width', '416');
    img.setAttribute('height', '416');
    img.setAttribute('loading', 'lazy');
    if (marker) img.setAttribute(marker, '');
    host.appendChild(img);
    return img;
  }

  function setHiddenAttr(el, attr, hidden) {
    if (!el) return;
    if (hidden) el.setAttribute(attr, '');
    else el.removeAttribute(attr);
  }

  /** Pick the slot-7 info image for the current FORMAT (frame ignored): exact
   *  format → any available format. Returns {url,...}|null. */
  function resolveInfoImage(infoImages, formatKey) {
    if (!infoImages || typeof infoImages !== 'object') return null;
    if (formatKey && infoImages[formatKey] && infoImages[formatKey].url) return infoImages[formatKey];
    return null;
  }

  /** Swap the hero (slot 1, on format+frame) + the slot-7 info image (on FORMAT
   *  only) for the current selection. */
  function applyComboSwap(gallery, galleryData, infoImages, detail) {
    const formatKey = detail && detail.formatKey;
    const frameKey = detail && detail.frameKey;

    // ── Hero (slot 1) — swaps on format + frame ──
    const heroImage = resolveCombo(galleryData.hero, formatKey, frameKey)
      || (galleryData.lifestyle && galleryData.lifestyle[0]) || null;
    if (heroImage) {
      const heroSlide =
        gallery.querySelector('[data-media-id$="-wall-art-slot-1"]') ||
        gallery.querySelector('.product__media-item.is-active') ||
        gallery.querySelector('.product__media-item');
      const heroContainer = heroSlide
        ? heroSlide.querySelector('.product-media-container') || heroSlide
        : null;
      setImg(ensureImg(heroContainer), heroImage);

      // Hero thumbnail (Slide-Thumbnails-<section>-0).
      const thumbs = gallery.querySelector('[id^="GalleryThumbnails-"]');
      const heroThumb = thumbs ? thumbs.querySelector('[data-media-position="1"] img, [id$="-0"] img') : null;
      setImg(heroThumb, heroImage);
    }

    // ── Slot 7 (the per-format featured slide shown 2nd) — info image, FORMAT only.
    // Per-shop static image from the theme editor; frame is intentionally ignored.
    const infoImage = resolveInfoImage(infoImages, formatKey);
    const infoSlide = gallery.querySelector('[data-format-featured-slide]');
    const infoThumbSlide = (gallery.querySelector('[id^="GalleryThumbnails-"]') || gallery)
      .querySelector('[data-format-featured-thumbnail-slide]');
    if (infoImage) {
      const infoContainer = infoSlide ? infoSlide.querySelector('.product-media-container') || infoSlide : null;
      setImg(ensureImg(infoContainer, 'data-format-featured-main'), infoImage);
      const infoThumbImg = ensureThumbImg(infoThumbSlide, 'data-format-featured-thumbnail');
      // Thumbnail prefers the smaller rendition when present.
      setImg(infoThumbImg, infoImage.thumb_url ? { url: infoImage.thumb_url, alt: infoImage.alt } : infoImage);
      setHiddenAttr(infoSlide, 'data-no-featured', false);
      setHiddenAttr(infoThumbSlide, 'data-no-featured', false);
    } else {
      // Blank setting for this format → hide slot 7 gracefully.
      setHiddenAttr(infoSlide, 'data-no-featured', true);
      setHiddenAttr(infoThumbSlide, 'data-no-featured', true);
    }
  }

  /** Fill the static, format-agnostic slots (lifestyle) once. */
  function applyStaticSlots(gallery, galleryData) {
    const lifestyle = Array.isArray(galleryData.lifestyle) ? galleryData.lifestyle : [];
    const thumbs = gallery.querySelector('[id^="GalleryThumbnails-"]');

    // Dynamic scene slots 2,3,4 → lifestyle[0..2].
    [2, 3, 4].forEach((slot, i) => {
      const image = lifestyle[i] || null;
      const slide = gallery.querySelector(`[data-wall-art-dynamic-slot="${slot}"]`);
      const thumbSlide = thumbs ? thumbs.querySelector(`[data-wall-art-dynamic-thumb-slot="${slot}"]`) : null;
      if (image) {
        const container = slide ? slide.querySelector('.product-media-container') || slide : null;
        setImg(ensureImg(container), image);
        const thumbImg = ensureThumbImg(thumbSlide);
        setImg(thumbImg, image);
        if (slide) slide.removeAttribute('hidden');
        if (thumbSlide) thumbSlide.removeAttribute('hidden');
      } else {
        if (slide) slide.setAttribute('hidden', '');
        if (thumbSlide) thumbSlide.setAttribute('hidden', '');
      }
    });

    // Static-image slot 6 → lifestyle[3] (the 4th lifestyle image).
    const image6 = lifestyle[3] || null;
    const slide6 = gallery.querySelector('[data-format-image-slide]');
    const thumbSlide6 = thumbs ? thumbs.querySelector('[data-format-image-thumbnail-slide]') : null;
    if (image6) {
      const container6 = slide6 ? slide6.querySelector('.product-media-container') || slide6 : null;
      setImg(ensureImg(container6, 'data-format-image-main'), image6);
      const thumbImg6 = ensureThumbImg(thumbSlide6, 'data-format-image-thumbnail');
      setImg(thumbImg6, image6);
      setHiddenAttr(slide6, 'data-no-image', false);
      setHiddenAttr(thumbSlide6, 'data-no-image', false);
    } else {
      setHiddenAttr(slide6, 'data-no-image', true);
      setHiddenAttr(thumbSlide6, 'data-no-image', true);
    }
  }

  function initForGallery(gallery) {
    const sectionRoot = findSectionRootForGallery(gallery);
    const sectionId = String(gallery.id || '').replace(/^MediaGallery-/, '');
    if (!sectionId) return;

    if (controllers.has(sectionId)) {
      try { controllers.get(sectionId).abort(); } catch { /* ignore */ }
      controllers.delete(sectionId);
    }

    const galleryData = readGallery(sectionRoot);
    if (!galleryData) return; // not a lite product — leave the gallery as-is.

    // The 2 per-format slot-7 info images set in the theme editor.
    const infoImages = readInfoImages(sectionRoot);

    const abortController = new AbortController();
    controllers.set(sectionId, abortController);

    // Static slots are combo-independent — fill them right away.
    applyStaticSlots(gallery, galleryData);

    const onChange = (event) => applyComboSwap(gallery, galleryData, infoImages, event && event.detail);
    sectionRoot.addEventListener('wallart:selection:changed', onChange, { signal: abortController.signal });
    document.addEventListener('wallart:selection:changed', onChange, { signal: abortController.signal });

    // Seed from any selection the picker already emitted (defer race), else from
    // the first available combo so the hero/slot-7 paint immediately.
    const seed = window.__wallArtCurrentSelection || {};
    applyComboSwap(gallery, galleryData, infoImages, seed);
  }

  function initAll(root) {
    const scope = (root && root.querySelectorAll) ? root : document;
    if (root && root.matches && root.matches('media-gallery')) initForGallery(root);
    scope.querySelectorAll('media-gallery').forEach((g) => initForGallery(g));
  }

  function destroySection(sectionId) {
    const ctrl = controllers.get(sectionId);
    if (!ctrl) return;
    try { ctrl.abort(); } catch { /* ignore */ }
    controllers.delete(sectionId);
  }

  function start() {
    initAll(document);

    document.addEventListener('shopify:section:load', (event) => {
      const target = event && event.target instanceof Element ? event.target : document;
      initAll(target);
    });

    document.addEventListener('shopify:section:unload', (event) => {
      const detailSectionId = String((event && event.detail && event.detail.sectionId) || '').trim();
      if (detailSectionId) { destroySection(detailSectionId); return; }
      const target = event && event.target instanceof Element ? event.target : null;
      if (!target) return;
      const gallery = (target.matches && target.matches('media-gallery'))
        ? target
        : (target.querySelector && target.querySelector('media-gallery'));
      if (!gallery) return;
      const derived = String(gallery.id || '').replace(/^MediaGallery-/, '');
      if (derived) destroySection(derived);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
